import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { MoreButtonCDS } from '@sharedCR/coreDesignSystem/Buttons';
import { DeleteIcon, EditPenIcon } from '@sharedCR/coreDesignSystem/icons';
import MenuPopoverCDS from '@sharedCR/coreDesignSystem/MenuPopoverCDS';
import { isEqual } from 'lodash';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import useForm from 'hooks/useForm';
import usePopover from 'hooks/usePopover';
import { getConditionalArray, getConditionalObject } from 'utils/data';
import { getPersonFullName } from 'utils/loanApplication';
import { getConfigInfo } from 'utils/loanManagement';

import {
  selectIsCICHistoryReasonExists,
  selectLoanFeeReasonHistoryByFeeTypeId,
} from 'store/loanApplications/selectors';

import { useOpenCICModal } from '../../../../ManageCICModal/hooks/useOpenCICModal';

import { getCICFormConfig } from './FeeEditMenu.config';

const iconDefaultProps = {
  size: IconSize[20],
};

const FeeEditMenu = ({
  feeTypeId,
  onDeleteFee,
  isDisabled,
  isDisabledDelete,
  isDisabledManageCIC,
  index,
}) => {
  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();
  const actionAnchorId = anchorEl ? `fee-action-popover-${feeTypeId}` : undefined;

  const isCICChangeReasonExists = useSelector(selectIsCICHistoryReasonExists(feeTypeId));
  const cicReasons = useSelector(selectLoanFeeReasonHistoryByFeeTypeId(feeTypeId), isEqual);

  const isDisabledManage = isDisabledManageCIC || !isCICChangeReasonExists || !feeTypeId;

  const { initialFormValues, fieldsConfig, schema } = useMemo(() => {
    const applicationData = cicReasons.reduce((acc, reason) => {
      acc[reason.loanCicFeeChangeReasonId] = {
        textNote: reason.note ?? '',
        deletionReason: reason.deletionReason ?? '',
        reasonStatus: reason.status ?? '',
        modifiedDate: reason.modifiedDate ?? '',
        modifiedBy: reason.modifiedBy ? getPersonFullName(reason.modifiedBy) : '',
      };

      return acc;
    }, {});

    return getConfigInfo({
      applicationData,
      config: getCICFormConfig(cicReasons),
      isNestedForm: true,
    });
  }, [cicReasons]);

  const formContext = useForm(initialFormValues, fieldsConfig);
  const { setSchema, setInitialFormValues, setForm } = formContext;

  const openCICModal = useOpenCICModal({ feeTypeId, formContext, cicReasons });

  const onDelete = useCallback(
    e => {
      onClearAnchorElement(e);

      const syntheticEvent = {
        ...e,
        currentTarget: {
          ...e.currentTarget,
          dataset: {
            index,
            ...getConditionalObject(feeTypeId, {
              feeTypeId,
            }),
          },
        },
      };

      onDeleteFee(syntheticEvent);
    },
    [onClearAnchorElement, index, feeTypeId, onDeleteFee],
  );

  const onManageCIC = useCallback(
    e => {
      onClearAnchorElement(e);
      openCICModal();
    },
    [onClearAnchorElement, openCICModal],
  );

  const items = useMemo(
    () => [
      {
        text: 'Manage CIC',
        onClick: onManageCIC,
        icon: <EditPenIcon {...iconDefaultProps} />,
        'data-id': 'manage-cic',
        ...getConditionalObject(isDisabledManage, {
          isDisabled: true,
          disableTooltip: 'No CIC reason to manage',
        }),
      },
      ...getConditionalArray(onDeleteFee, [
        {
          text: 'Delete',
          onClick: onDelete,
          icon: <DeleteIcon {...iconDefaultProps} />,
          'data-id': 'delete-fee',
          isDisabled: isDisabledDelete,
        },
      ]),
    ],
    [onManageCIC, isDisabledManage, onDeleteFee, onDelete, isDisabledDelete],
  );

  useEffect(() => {
    setInitialFormValues(initialFormValues);
    setForm(initialFormValues);
  }, [initialFormValues, setInitialFormValues, setForm]);

  useEffect(() => {
    setSchema(schema);
  }, [schema, setSchema]);

  return (
    <>
      <MoreButtonCDS
        aria-describedby={actionAnchorId}
        disabled={isDisabled || (isDisabledManage && isDisabledDelete)}
        isSelected={!!anchorEl}
        onClick={onSetAnchorElement}
      />

      <MenuPopoverCDS
        anchorEl={anchorEl}
        id={actionAnchorId}
        items={items}
        onClose={onClearAnchorElement}
      />
    </>
  );
};

FeeEditMenu.propTypes = {
  index: PropTypes.number.isRequired,
  onDeleteFee: PropTypes.func.isRequired,
  feeTypeId: PropTypes.string,
  isDisabled: PropTypes.bool,
  isDisabledDelete: PropTypes.bool,
  isDisabledManageCIC: PropTypes.bool,
};

export default FeeEditMenu;
